'use client';

import { useRef } from 'react';

import { useAui, useAuiState } from '@assistant-ui/react';
import { get, isString, startCase, truncate } from 'lodash';
import {
  BanIcon,
  CircleHelpIcon,
  ClockIcon,
  ShieldQuestionIcon,
  TriangleAlertIcon,
  type LucideIcon,
} from 'lucide-react';
import { useTranslations } from 'next-intl';

import {
  isSupersededApproval,
  resolveApprovalCardState,
  type ApprovalCardState,
  type ApprovalPartState,
} from '@/components/assistant-ui/approval/approval-state';
import { GATE_STYLES } from '@/components/assistant-ui/approval/gate-styles';
import { actionIcon } from '@/components/assistant-ui/approval/gated-actions';
import { useConfirmationShown } from '@/components/assistant-ui/approval/use-confirmation-shown';
import {
  isMessageLive,
  wasCutOffAfterApproval,
} from '@/components/assistant-ui/message-part-utils';
import { ToolDebugInfo } from '@/components/assistant-ui/tool-debug-info';
import { useToolGroup } from '@/components/assistant-ui/tool-group-provider';
import { getToolStatusFlags, type ToolStatus } from '@/components/assistant-ui/tool-status';
import { useConfigValue } from '@/components/providers/config-provider';
import { useReadOnly } from '@/components/providers/read-only-provider';
import { ActionButton, CancelActionButton } from '@/components/ui/action-button';
import {
  ActionCard,
  ActionCardActions,
  ActionCardHeader,
  ActionCardOutcome,
} from '@/components/ui/action-card';
import { FocusHandoffRegion } from '@/components/ui/focus-handoff';
import { isUnknownOutcomeResult } from '@/lib/constants/tool-outcomes';
import type { ApprovalActionSpec } from '@/lib/types/tenant';
import { WEBSITE_HEADERS } from '@/lib/website-auth/constants';

/**
 * Builds the key→value rows shown on the approval card so the user can see what they're about to
 * approve (e.g. `Record Id: 123`). Includes only primitive fields — nested objects/arrays don't
 * render usefully in the compact card — caps at 8 rows, truncates values over 80 chars, and
 * title-cases the keys. `args` is `unknown` because tool call-arguments are arbitrary wire JSON.
 */
export function summarizeApprovalArgs(
  args: unknown,
): { key: string; label: string; value: string }[] {
  if (!args || typeof args !== 'object' || Array.isArray(args)) return [];
  return (
    Object.entries(args as Record<string, unknown>)
      // Older persisted tool calls may still contain internal transport fields.
      .filter(
        ([key, v]) =>
          !key.toLowerCase().startsWith(WEBSITE_HEADERS.PREFIX) &&
          key.toLowerCase() !== WEBSITE_HEADERS.INTERNAL_AUTH &&
          ['string', 'number', 'boolean'].includes(typeof v),
      )
      .slice(0, 8)
      .map(([key, v]) => {
        const raw = String(v);
        return {
          key,
          label: startCase(key),
          value: truncate(raw, { length: 80, omission: '…' }),
        };
      })
  );
}

export function humanizeToolName(toolName: string): string {
  return startCase(toolName);
}

/** The tenant's line for this action, or the generic one. */
function actionCopy({
  labels,
  toolName,
  key,
  fallback,
}: {
  labels: ReturnType<typeof useTranslations>;
  toolName: string;
  key: string;
  fallback: string;
}): string {
  const path = `actions.${toolName}.${key}`;
  return labels.has(path) ? labels(path) : fallback;
}

export interface GenericApprovalCardViewProps {
  /** A toolkit's own question, when it knows more than the call's arguments, such as the hotel
   *  behind a booking number. */
  title?: string;
  toolName: string;
  args: unknown;
  children?: React.ReactNode;
  footnote?: React.ReactNode;
  /** The action's own icon, as its trail row shows it; without one, a generic question. */
  icon?: LucideIcon;
  state: ApprovalCardState;
  /** Approved and still executing. */
  running?: boolean;
  executionFailed?: boolean;
  /** The tool's own copy for an expected outcome other than plain done, when it has one. */
  outcomeCopyKey?: string;
  /** Approved, but whether it went through is unknown: the supplier could not say, or the run
   *  ended before a result arrived. */
  unconfirmed?: boolean;
  /** Declined only because the guest moved on: reads as skipped, not as their decline. */
  superseded?: boolean;
  onApprove?: () => void;
  onDecline?: () => void;
}

type Outcome = { icon: LucideIcon; tone: 'ok' | 'error' | 'live' | 'muted'; text: string };

function approvalOutcome({
  state,
  running,
  executionFailed,
  outcomeCopyKey,
  unconfirmed,
  superseded,
  copy,
  t,
}: {
  state: ApprovalCardState;
  running?: boolean;
  executionFailed?: boolean;
  outcomeCopyKey?: string;
  unconfirmed?: boolean;
  superseded?: boolean;
  copy: (args: { key: string; fallback: string }) => string;
  t: (key: string) => string;
}): Outcome | undefined {
  if (executionFailed) return { icon: BanIcon, tone: 'error', text: t('executionFailed') };
  if (unconfirmed) {
    return {
      icon: CircleHelpIcon,
      tone: 'live',
      text: copy({ key: 'unconfirmed', fallback: t('unconfirmed') }),
    };
  }
  switch (state) {
    case 'pending':
      return undefined;
    case 'approved':
    case 'auto-approved': {
      if (running) return undefined;
      const own = outcomeCopyKey && copy({ key: outcomeCopyKey, fallback: '' });
      if (own) return { icon: ClockIcon, tone: 'live', text: own };
      return {
        icon: GATE_STYLES.actionConfirmed.icon,
        tone: 'ok',
        text: copy({
          key: 'done',
          fallback: t(state === 'approved' ? 'approved' : 'autoApproved'),
        }),
      };
    }
    case 'denied':
      return superseded
        ? { icon: GATE_STYLES.actionSkipped.icon, tone: 'muted', text: t('declinedSuperseded') }
        : {
            icon: GATE_STYLES.actionDeclined.icon,
            tone: 'muted',
            text: copy({ key: 'declined', fallback: t('declined') }),
          };
    case 'expired':
      return { icon: ClockIcon, tone: 'muted', text: t('expired') };
    case 'cancelled':
      return { icon: BanIcon, tone: 'muted', text: t('cancelled') };
    case 'unanswered':
      return {
        icon: GATE_STYLES.actionSkipped.icon,
        tone: 'muted',
        text: copy({ key: 'unanswered', fallback: t('unanswered') }),
      };
  }
}

/** Once answered, only the footer changes: the answer takes the buttons' place, and the
 *  question and details above stay exactly as the guest saw them when deciding. */
export function GenericApprovalCardView({
  toolName,
  args,
  children,
  footnote,
  title,
  icon,
  state,
  running,
  executionFailed,
  outcomeCopyKey,
  unconfirmed,
  superseded,
  onApprove,
  onDecline,
}: GenericApprovalCardViewProps) {
  const t = useTranslations('approvalCard');
  // Tenant-specific labels fall back to humanized identifiers.
  const labels = useTranslations(useConfigValue('approvalLabelsNamespace') ?? 'approvalCard');
  const toolLabel = labels.has(`tools.${toolName}`)
    ? labels(`tools.${toolName}`)
    : humanizeToolName(toolName);
  const action = useConfigValue<Record<string, ApprovalActionSpec>>('approvalActions')?.[toolName];
  function copy({ key, fallback }: { key: string; fallback: string }) {
    return actionCopy({ labels, toolName, key, fallback });
  }
  // The generic line asks for an answer, so it goes once the card has one; a tenant's own body
  // describes the action and stays.
  const body = copy({ key: 'body', fallback: state === 'pending' ? t('title') : '' });
  function isShownField({ key }: { key: string }) {
    return !action?.fields || action.fields.includes(key);
  }
  const rows = summarizeApprovalArgs(args).filter(isShownField);
  const destructive = action?.destructive;
  const busy = (state === 'approved' || state === 'auto-approved') && running;
  const outcome = approvalOutcome({
    state,
    running,
    executionFailed,
    outcomeCopyKey,
    unconfirmed,
    superseded,
    copy,
    t,
  });
  const confirm = copy({ key: 'confirm', fallback: t('approve') });
  const focusHandoffRef = useRef(false);

  return (
    <ActionCard data-approval-state={busy ? 'running' : state}>
      <ActionCardHeader
        icon={icon ?? (destructive ? TriangleAlertIcon : ShieldQuestionIcon)}
        tone={destructive ? 'destructive' : 'default'}
        title={title ?? copy({ key: 'title', fallback: toolLabel })}
      />

      {/* What it acts on, the tenant's details, what will happen, then any closing note: the order
          WhatsApp sends them in. `empty`: a details component may render nothing. */}
      <div className="mt-2 flex flex-col gap-3 text-sm">
        {rows.length > 0 && (
          <dl className="flex flex-col gap-1.5">
            {rows.map(({ key, label, value }) => (
              // Label and value side by side, as the reservation card writes its order number:
              // pushed to the far edge, a value loses the label it belongs to.
              <div key={key} className="flex items-baseline gap-1.5">
                <dt className="shrink-0">
                  {labels.has(`args.${key}`) ? labels(`args.${key}`) : label}
                </dt>
                {/* Wrapped rather than truncated: a hover title never reaches touch or keyboard. */}
                <dd className="min-w-0 font-semibold wrap-break-word">
                  <bdi>{value}</bdi>
                </dd>
              </div>
            ))}
          </dl>
        )}
        {children && <div className="empty:hidden">{children}</div>}
        {body && <p className="whitespace-pre-line">{body}</p>}
        {footnote && <div className="empty:hidden">{footnote}</div>}
      </div>

      <ActionCardActions divided>
        <FocusHandoffRegion key={outcome ? 'outcome' : 'actions'} handoffRef={focusHandoffRef}>
          {outcome ? (
            <ActionCardOutcome icon={outcome.icon} tone={outcome.tone}>
              {outcome.text}
            </ActionCardOutcome>
          ) : (
            <>
              <ActionButton
                tone={destructive ? 'destructive' : 'accent'}
                busy={busy}
                onClick={onApprove}
                className="flex-1"
              >
                {confirm}
              </ActionButton>
              <CancelActionButton
                label={copy({ key: 'decline', fallback: t('decline') })}
                onCancel={onDecline}
                disabled={busy}
              />
            </>
          )}
        </FocusHandoffRegion>
      </ActionCardActions>
    </ActionCard>
  );
}

export interface GenericApprovalCardProps {
  title?: string;
  toolName: string;
  toolCallId: string;
  args: unknown;
  children?: React.ReactNode;
  footnote?: React.ReactNode;
  approval: ApprovalPartState;
  status: ToolStatus;
  result?: unknown;
  /** False while the card may still give way to another step; its shown event waits until then. */
  confirmationSettled?: boolean;
}

// Backend approval tools may override this card through a toolkit render entry.
export function GenericApprovalCard({
  toolName,
  toolCallId,
  args,
  children,
  footnote,
  title,
  approval,
  status,
  result,
  confirmationSettled = true,
}: GenericApprovalCardProps) {
  const aui = useAui();
  const trailIcon = useToolGroup().getTrailConfig?.(toolName)?.icons?.[0];
  const messageLive = useAuiState(({ message }) => isMessageLive(message?.status));
  const resolved = resolveApprovalCardState(approval);
  // A transcript has nobody left to answer, so an open gate there is one that never was.
  const state = useReadOnly() && resolved === 'pending' ? 'unanswered' : resolved;
  useConfirmationShown({
    toolCallId,
    toolName,
    shown: state === 'pending' && confirmationSettled,
  });
  const approved = approval.approved === true && !approval.resolution;
  // Only this message's own run can still produce the result. A run cut off after approval
  // would otherwise spin forever, and the thread-wide flag would restart the spinner on every
  // later reply.
  const running = getToolStatusFlags(status).isRunning && messageLive;
  const unconfirmed =
    (approved && isUnknownOutcomeResult(result)) ||
    wasCutOffAfterApproval({ approval, result, messageLive });
  const resultStatus = get(result, 'status');

  return (
    <ToolDebugInfo toolName={toolName} args={args} result={result} status={status}>
      <GenericApprovalCardView
        toolName={toolName}
        args={args}
        footnote={footnote}
        title={title}
        icon={trailIcon && actionIcon(trailIcon)}
        state={state}
        running={running}
        executionFailed={approved && !unconfirmed && getToolStatusFlags(status).isError}
        outcomeCopyKey={isString(resultStatus) ? `outcome.${resultStatus}` : undefined}
        unconfirmed={unconfirmed}
        superseded={isSupersededApproval(approval)}
        onApprove={function approve() {
          aui.part.respondToToolApproval({ approved: true });
        }}
        onDecline={function decline() {
          aui.part.respondToToolApproval({ approved: false });
        }}
      >
        {children}
      </GenericApprovalCardView>
    </ToolDebugInfo>
  );
}
